import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import FormInput from 'shared/components/forms/FormInput';
import FormPhoneInput from 'shared/components/forms/FormPhoneInput';
import GeolocationCapture from 'features/onboarding/components/forms/GeolocationCapture';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useRegisterStepNav } from 'features/onboarding/context/StepNavContext';
import { useOnboardingOptions } from 'features/onboarding/hooks/useOnboardingOptions';
import { buildCityOptions } from 'features/onboarding/utils/cityLabel';
import { stepContactSchema } from 'features/onboarding/schemas';
import { createPotentialLead } from 'features/onboarding/api/savePotentialLead';
import type { PlaceDetails } from 'features/onboarding/services/geocoding.service';

interface LocalState {
  commercialName: string;
  ownerName: string;
  phoneNumber: string;
  /** Resolved from the pin, never typed. */
  address: string;
  /** City resolved from the pin, matched against the HAAT city list. */
  location: string;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Step 1 — the short introduction: trade name, contact person, mobile number,
 * and the store location on the map. Finishing it opens a potential lead and
 * moves on to step 2, which asks for the SMS code before showing its form.
 */
const Step2Contact: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data, updateSection, setMetadata, goNext, goBack, registerStepGuard } =
    useOnboarding();
  const { data: options } = useOnboardingOptions();

  const [local, setLocal] = useState<LocalState>({
    commercialName: data.businessInfo.commercialName,
    ownerName: data.businessInfo.ownerName,
    phoneNumber: data.businessInfo.phoneNumber,
    address: data.businessInfo.address,
    location: data.businessInfo.location,
    latitude: data.businessInfo.latitude ?? null,
    longitude: data.businessInfo.longitude ?? null,
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const cityOptions = useMemo(
    () => buildCityOptions(options?.areas, i18n.language),
    [options, i18n.language],
  );

  const setField = <K extends keyof LocalState>(key: K, value: LocalState[K]) =>
    setLocal((s) => ({ ...s, [key]: value }));

  const validate = (): boolean => {
    const result = stepContactSchema.safeParse(local);
    if (result.success) {
      setErrors({});
      return true;
    }
    const next: Record<string, string | undefined> = {};
    result.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === 'string' && !next[key]) next[key] = issue.message;
    });
    setErrors(next);
    return false;
  };

  const persist = () => {
    updateSection('businessInfo', {
      commercialName: local.commercialName,
      ownerName: local.ownerName,
      phoneNumber: local.phoneNumber,
      address: local.address,
      location: local.location,
      latitude: local.latitude,
      longitude: local.longitude,
    });
  };

  const handleNext = async () => {
    if (!validate()) return;
    persist();

    await createPotentialLead({
      commercialName: local.commercialName,
      ownerName: local.ownerName,
      phoneNumber: local.phoneNumber,
      address: local.address,
      location: local.location,
      latitude: local.latitude,
      longitude: local.longitude,
    });

    goNext();
  };

  // Editing the number invalidates an earlier verification and the challenge
  // that was sent to the old one, so the next step asks for a fresh code.
  const handlePhoneChange = (value: string) => {
    setField('phoneNumber', value);
    if (data.metadata.verifiedPhone === value) return;
    const stale =
      data.metadata.phoneVerified ||
      !!data.metadata.verifiedPhone ||
      !!data.metadata.otpChallengeId;
    if (stale) {
      setMetadata({
        phoneVerified: false,
        verifiedPhone: undefined,
        phoneVerificationToken: undefined,
        otpChallengeId: undefined,
        otpSentTo: undefined,
      });
    }
  };

  const handleCapture = (
    lat: number,
    lng: number,
    details?: PlaceDetails | null,
  ) => {
    setLocal((s) => {
      let nextAddress = s.address;
      let nextLocation = s.location;

      if (details) {
        const street = [details.street, details.houseNumber]
          .filter((v) => v && v.trim())
          .join(' ')
          .trim();
        if (street) nextAddress = street;
        else if (details.formattedAddress) nextAddress = details.formattedAddress;

        if (details.city) {
          const match = cityOptions.find(
            (o) =>
              o.value.localeCompare(details.city, undefined, {
                sensitivity: 'accent',
              }) === 0 ||
              o.label.localeCompare(details.city, undefined, {
                sensitivity: 'accent',
              }) === 0,
          );
          // No HAAT area matches: keep Google's city so the business step has
          // something to show and the sales team something to work with.
          nextLocation = match ? match.value : details.city;
        }
      }

      return {
        ...s,
        latitude: lat,
        longitude: lng,
        address: nextAddress,
        location: nextLocation,
      };
    });
  };

  useRegisterStepNav({
    onBack: goBack,
    onNext: handleNext,
    hideBack: true,
    nextLabel: t('nav.contact') as string,
  });

  const validateRef = useRef(validate);
  validateRef.current = validate;
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    registerStepGuard(() => {
      if (!validateRef.current()) return false;
      persistRef.current();
      return true;
    });
    return () => registerStepGuard(null);
  }, [registerStepGuard]);

  return (
    <StepCard
      title={t('contact.title')}
      subtitle={t('contact.subtitle') as string}
    >
      <div className="flex flex-col gap-6">
        <FormInput
          label={t('businessInfo.commercialName')}
          placeholder={t('businessInfo.commercialNamePlaceholder') as string}
          hint={t('businessInfo.commercialNameHint') as string}
          required
          value={local.commercialName}
          onChange={(e) => setField('commercialName', e.target.value)}
          error={errors.commercialName}
        />

        <FormPhoneInput
          name="phoneNumber"
          label={t('businessInfo.phoneNumber')}
          required
          value={local.phoneNumber}
          onChange={handlePhoneChange}
          error={errors.phoneNumber}
        />

        <FormInput
          label={t('businessInfo.contactPerson')}
          placeholder={t('businessInfo.contactPersonPlaceholder') as string}
          required
          value={local.ownerName}
          onChange={(e) => setField('ownerName', e.target.value)}
          error={errors.ownerName}
        />

        <GeolocationCapture
          lat={local.latitude}
          lng={local.longitude}
          address={[local.address, local.location].filter(Boolean).join(', ')}
          resolvedAddress={[local.address, local.location]
            .filter(Boolean)
            .join(', ')}
          error={errors.latitude}
          onCapture={handleCapture}
          onClear={() =>
            setLocal((s) => ({ ...s, latitude: null, longitude: null }))
          }
        />
      </div>
    </StepCard>
  );
};

export default Step2Contact;
